import {useCallback, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useFormikContext} from 'formik';
import {useSelector} from 'react-redux';
import {getPriceGroupsByPackage, findPriceGroupByRange, createPriceGroup, updatePriceGroup, getPriceGroupImpact} from '../../../../../../../redux/subscription/subscriptionActions';
import {bounceMessage} from '../../../../../../../layout/userMessages/UserMessage';
import {TShow} from '../../../../../../../component/prompotionPopup/PromotionPopup';
import {SubscriptionFormValues} from '../../../createSubscriptionForm.types';
import {TState} from '../../../../../../../redux/store.type';
import {PricingGroupFormValues} from '../../../../pricingGroupPop/PricingGroupPop';
import {formatAffectedPlansMessage} from '../../../../../utils/formatAffectedPlansMessage';

export const usePriceGroups = (priceGroupPopShow: TShow) => {
    const {t} = useTranslation('subscriptions');
    const {values, setFieldValue} = useFormikContext<SubscriptionFormValues>();
    const priceGroups = useSelector((state: TState) => state.subscription.priceGroups || []);
    const getPriceGroupsByPackageXHR = useSelector((state: TState) => state.subscription.getPriceGroupsByPackageXHR);

    useEffect(() => {
        if(!getPriceGroupsByPackageXHR) {
            getPriceGroupsByPackage();
        }
    }, []);

    // ============ HELPERS ============
    const addPriceGroupToForm = useCallback((priceGroupId: number) => {
        const currentPriceGroupIds = values.priceGroupIds || [];
        if(!currentPriceGroupIds.includes(priceGroupId)) {
            setFieldValue('priceGroupIds', [...currentPriceGroupIds, priceGroupId]);
        }
    }, [values.priceGroupIds, setFieldValue]);

    const showConfirmation = useCallback((message: string, onConfirm: () => Promise<void>): Promise<boolean> => {
        return new Promise((resolve) => {
            bounceMessage(
                message,
                'choice',
                {
                    text: t('continue'),
                    onClick: () => {
                        void onConfirm().then(() => resolve(true));
                    }
                },
                {
                    text: t('cancel'),
                    onClick: () => resolve(false)
                }
            );
        });
    }, [t]);

    const closePopupAndAddToForm = useCallback((priceGroupId: number) => {
        addPriceGroupToForm(priceGroupId);
        priceGroupPopShow.close();
    }, [addPriceGroupToForm, priceGroupPopShow]);

    // ============ EDIT FLOW ============
    const handleEditPriceGroup = useCallback(async (
        priceGroupId: number,
        formValues: PricingGroupFormValues
    ): Promise<boolean> => {
        const minPrice = parseFloat(formValues.startPrice);
        const maxPrice = parseFloat(formValues.endPrice);

        const impactInfo = await getPriceGroupImpact(priceGroupId);
        const affectedCount = impactInfo?.affectedPlans || 0;

        const performUpdate = async () => {
            await updatePriceGroup(priceGroupId, {
                name: formValues.name,
                minPrice,
                maxPrice,
                skipConflictCheck: true
            });
            priceGroupPopShow.close();
        };

        if(affectedCount > 0) {
            const message = formatAffectedPlansMessage(
                t,
                affectedCount,
                impactInfo?.affectedPlanNames,
                impactInfo?.moreAffectedPlansCount
            );
            return showConfirmation(message, performUpdate);
        }

        await performUpdate();
        return true;
    }, [t, priceGroupPopShow, showConfirmation]);

    // ============ CREATE FLOW ============
    const handleCreatePriceGroup = useCallback(async (formValues: PricingGroupFormValues): Promise<boolean> => {
        const minPrice = parseFloat(formValues.startPrice);
        const maxPrice = parseFloat(formValues.endPrice);

        try {
            // Check if a price group with this range already exists
            const existingRes = await findPriceGroupByRange(minPrice, maxPrice);

            if(existingRes?.priceGroup) {
                // Range already exists - ask user if they want to use it
                const existingGroup = existingRes.priceGroup;
                const groupName = existingRes.existingGroup?.title || existingGroup.title || t('pricingGroup');
                const affectedCount = existingRes.affectedPlansCount || 0;

                const existsMessage = t('priceGroupExistsMessage', {groupName});
                const affectedMessage = formatAffectedPlansMessage(
                    t,
                    affectedCount,
                    existingRes.affectedPlanNames,
                    existingRes.moreAffectedPlansCount
                );
                const message = [existsMessage, affectedMessage]
                    .filter(Boolean)
                    .join(' · ');

                return showConfirmation(message, async () => {
                    await updatePriceGroup(existingGroup.spgId, {title: formValues.name});
                    closePopupAndAddToForm(existingGroup.spgId);
                });
            }

            // No existing range - create new price group
            const createRes = await createPriceGroup({title: formValues.name, minPrice, maxPrice});
            if(createRes?.priceGroup) {
                closePopupAndAddToForm(createRes.priceGroup.spgId);
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error creating price group:', error);
            return false;
        }
    }, [t, showConfirmation, closePopupAndAddToForm]);

    // ============ UNIFIED HANDLER ============
    const handleSavePriceGroup = useCallback(async (
        formValues: PricingGroupFormValues,
        priceGroupId?: number | null
    ): Promise<boolean> => {
        
        if(priceGroupId) {
            return await handleEditPriceGroup(priceGroupId, formValues);
        }
        return await handleCreatePriceGroup(formValues);
        
    }, [handleEditPriceGroup, handleCreatePriceGroup]);

    return {
        priceGroups,
        handleSavePriceGroup
    };
};

